// SMS Service for OTP functionality using Twilio Verify
// This provides better security and built-in verification

// ===== TWILIO VERIFY IMPLEMENTATION (Recommended) =====
export const twilioVerify = {
  accountSid: process.env.REACT_APP_TWILIO_ACCOUNT_SID || 'ACb41612d83fce4f2c070a8ff7e61d387d',
  authToken: process.env.REACT_APP_TWILIO_AUTH_TOKEN || '7e7fb96c43bffac24aea31c1c7f73433',
  verifyServiceSid: process.env.REACT_APP_TWILIO_VERIFY_SERVICE_SID || 'VA3d6a8053cb9261c614b29767529311f3',

  sendOTP: async (mobile: string): Promise<string> => {
    try {
      // Check if Twilio is properly configured
      if (twilioVerify.authToken === '[AuthToken]' || !twilioVerify.authToken) {
        console.warn('⚠️ Twilio Auth Token not configured. Switching to demo mode.');
        throw new Error('Twilio Auth Token not configured. Please set REACT_APP_TWILIO_AUTH_TOKEN');
      }

      console.log('🔐 Attempting to send OTP via Twilio Verify...');
      console.log('📱 Mobile:', mobile);
      console.log('🆔 Account SID:', twilioVerify.accountSid);
      console.log('🔑 Auth Token:', twilioVerify.authToken ? '***configured***' : 'NOT CONFIGURED');
      console.log('🆔 Service SID:', twilioVerify.verifyServiceSid);

      const response = await fetch(`https://verify.twilio.com/v2/Services/${twilioVerify.verifyServiceSid}/Verifications`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${btoa(`${twilioVerify.accountSid}:${twilioVerify.authToken}`)}`,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          To: `+91${mobile}`, // Add country code for India
          Channel: 'sms',
        }),
      });

      const data = await response.json();
      console.log('📡 Twilio API Response:', data);

      if (!response.ok) {
        console.error('❌ Twilio Verify error:', data);
        
        // Provide specific error messages
        if (data.code === 60200) {
          throw new Error('Invalid phone number format. Please enter a valid 10-digit mobile number.');
        } else if (data.code === 60202) {
          throw new Error('Invalid Auth Token. Please check your Twilio credentials.');
        } else if (data.code === 60203) {
          throw new Error('Invalid Account SID. Please check your Twilio credentials.');
        } else if (data.code === 60204) {
          throw new Error('Invalid Verify Service SID. Please check your Twilio configuration.');
        } else if (data.code === 60205) {
          throw new Error('Phone number not verified. Please add this number to your Twilio verified numbers.');
        } else {
          throw new Error(data.message || 'Failed to send OTP. Please try again.');
        }
      }

      console.log('✅ Twilio Verify OTP sent successfully:', data.sid);
      
      // Store verification SID for later verification
      localStorage.setItem('twilioVerificationSid', data.sid);
      localStorage.setItem('twilioMobile', mobile);
      localStorage.setItem('twilioTimestamp', Date.now().toString());

      return data.sid; // Return verification SID
    } catch (error: any) {
      console.error('❌ Twilio Verify SMS error:', error);
      
      // If it's a configuration error, suggest demo mode
      if (error.message && error.message.includes('Auth Token not configured')) {
        console.log('💡 Tip: Set REACT_APP_TWILIO_AUTH_TOKEN in your .env file or switch to demo mode');
      }
      
      throw error;
    }
  },

  verifyOTP: async (mobile: string, otp: string): Promise<boolean> => {
    try {
      const verificationSid = localStorage.getItem('twilioVerificationSid');
      const storedMobile = localStorage.getItem('twilioMobile');
      const timestamp = localStorage.getItem('twilioTimestamp');

      if (!verificationSid || !storedMobile || !timestamp) {
        throw new Error('No verification session found. Please send OTP again.');
      }

      // Check if verification is expired (10 minutes)
      const now = Date.now();
      const verificationTime = parseInt(timestamp);
      if (now - verificationTime > 10 * 60 * 1000) {
        localStorage.removeItem('twilioVerificationSid');
        localStorage.removeItem('twilioMobile');
        localStorage.removeItem('twilioTimestamp');
        throw new Error('OTP verification expired. Please request a new OTP.');
      }

      console.log('🔍 Verifying OTP with Twilio...');

      // Verify OTP with Twilio
      const response = await fetch(`https://verify.twilio.com/v2/Services/${twilioVerify.verifyServiceSid}/VerificationCheck`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${btoa(`${twilioVerify.accountSid}:${twilioVerify.authToken}`)}`,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          To: `+91${mobile}`,
          Code: otp,
        }),
      });

      const data = await response.json();
      console.log('📡 Twilio Verify check response:', data);

      if (!response.ok) {
        console.error('❌ Twilio Verify check error:', data);
        throw new Error(data.message || 'Failed to verify OTP');
      }

      console.log('✅ Twilio Verify check result:', data.status);

      if (data.status === 'approved') {
        // Clear verification data after successful verification
        localStorage.removeItem('twilioVerificationSid');
        localStorage.removeItem('twilioMobile');
        localStorage.removeItem('twilioTimestamp');
        return true;
      } else if (data.status === 'pending') {
        throw new Error('Invalid OTP. Please check and try again.');
      } else {
        throw new Error('OTP verification failed. Please try again.');
      }
    } catch (error) {
      console.error('❌ Twilio Verify verification error:', error);
      throw error;
    }
  },
};

// ===== FALLBACK: DEMO MODE (for testing without Twilio) =====
export const demoOTP = {
  sendOTP: async (mobile: string): Promise<string> => {
    const otp = Math.floor(100000 + Math.random() * 900000).toString();
    
    // Store OTP temporarily
    localStorage.setItem('demoOTP', otp);
    localStorage.setItem('demoMobile', mobile);
    localStorage.setItem('demoTimestamp', Date.now().toString());
    
    return otp;
  },

  verifyOTP: (mobile: string, otp: string): boolean => {
    const storedOTP = localStorage.getItem('demoOTP');
    const storedMobile = localStorage.getItem('demoMobile');
    const timestamp = localStorage.getItem('demoTimestamp');
    
    if (!storedOTP || !storedMobile || !timestamp) {
      return false;
    }

    // Check if OTP is expired (5 minutes for demo)
    const now = Date.now();
    const otpTime = parseInt(timestamp);
    if (now - otpTime > 5 * 60 * 1000) {
      localStorage.removeItem('demoOTP');
      localStorage.removeItem('demoMobile');
      localStorage.removeItem('demoTimestamp');
      return false;
    }

    // Verify OTP
    if (mobile === storedMobile && otp === storedOTP) {
      localStorage.removeItem('demoOTP');
      localStorage.removeItem('demoMobile');
      localStorage.removeItem('demoTimestamp');
      return true;
    }

    return false;
  },
};

// ===== MAIN SMS SERVICE (Choose your preferred provider) =====
export const smsService = {
  // Change this to your preferred SMS service
  provider: 'auto', // Options: 'twilio', 'demo', 'auto'

  // Add options: { forceProvider?: 'twilio' | 'demo' }
  sendOTP: async (mobile: string, options?: { forceProvider?: 'twilio' | 'demo' }): Promise<string> => {
    try {
      // Auto-detect provider, or force if specified
      let currentProvider = smsService.provider;
      if (options && options.forceProvider) {
        currentProvider = options.forceProvider;
      } else if (currentProvider === 'auto') {
        if (smsService.isTwilioConfigured()) {
          currentProvider = 'twilio';
          console.log('🔐 Using Twilio Verify for OTP');
        } else {
          currentProvider = 'demo';
          console.log('🔄 Auto-switching to demo mode (Twilio not configured)');
        }
      }

      switch (currentProvider) {
        case 'twilio':
          return await twilioVerify.sendOTP(mobile);
        case 'demo':
        default:
          return await demoOTP.sendOTP(mobile);
      }
    } catch (error: any) {
      console.error('❌ SMS service error:', error);
      // If Twilio fails, fallback to demo mode only if not forced
      if (!options?.forceProvider && smsService.provider === 'twilio' && error.message && error.message.includes('Auth Token not configured')) {
        console.log('🔄 Falling back to demo mode due to Twilio configuration issue');
        return await demoOTP.sendOTP(mobile);
      }
      throw error;
    }
  },

  verifyOTP: async (mobile: string, otp: string, options?: { forceProvider?: 'twilio' | 'demo' }): Promise<boolean> => {
    try {
      // Auto-detect provider, or force if specified
      let currentProvider = smsService.provider;
      if (options && options.forceProvider) {
        currentProvider = options.forceProvider;
      } else if (currentProvider === 'auto') {
        if (smsService.isTwilioConfigured()) {
          currentProvider = 'twilio';
        } else {
          currentProvider = 'demo';
        }
      }

      switch (currentProvider) {
        case 'twilio':
          return await twilioVerify.verifyOTP(mobile, otp);
        case 'demo':
        default:
          return demoOTP.verifyOTP(mobile, otp);
      }
    } catch (error: any) {
      console.error('❌ OTP verification error:', error);
      // If Twilio fails, fallback to demo mode only if not forced
      if (!options?.forceProvider && smsService.provider === 'twilio' && error.message && error.message.includes('Auth Token not configured')) {
        console.log('🔄 Falling back to demo mode for verification');
        return demoOTP.verifyOTP(mobile, otp);
      }
      throw error;
    }
  },

  // Helper method to check if Twilio is configured
  isTwilioConfigured: (): boolean => {
    const accountSid = process.env.REACT_APP_TWILIO_ACCOUNT_SID;
    const authToken = process.env.REACT_APP_TWILIO_AUTH_TOKEN;
    const verifyServiceSid = process.env.REACT_APP_TWILIO_VERIFY_SERVICE_SID;
    
    return !!(accountSid && 
              authToken && 
              authToken !== '[AuthToken]' &&
              verifyServiceSid);
  },

  // Helper method to get current provider status
  getProviderStatus: (): string => {
    if (smsService.provider === 'auto') {
      return smsService.isTwilioConfigured() ? 'twilio' : 'demo';
    }
    return smsService.provider;
  },
};

// ===== LEGACY SMS SERVICES (kept for reference) =====

// Basic Twilio SMS (not recommended for OTP)
export const twilioSMS = {
  accountSid: process.env.REACT_APP_TWILIO_ACCOUNT_SID || '',
  authToken: process.env.REACT_APP_TWILIO_AUTH_TOKEN || '',
  fromNumber: process.env.REACT_APP_TWILIO_FROM_NUMBER || '',

  sendOTP: async (mobile: string, otp: string): Promise<void> => {
    try {
      const response = await fetch(`https://api.twilio.com/2010-04-01/Accounts/${twilioSMS.accountSid}/Messages.json`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${btoa(`${twilioSMS.accountSid}:${twilioSMS.authToken}`)}`,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          To: `+91${mobile}`,
          From: twilioSMS.fromNumber,
          Body: `Your SEOExpert Admin OTP is: ${otp}. Valid for 5 minutes.`,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send SMS');
      }

      console.log('SMS sent successfully via Twilio');
    } catch (error) {
      console.error('Twilio SMS error:', error);
      throw new Error('Failed to send OTP');
    }
  },
};

// Fast2SMS implementation (India)
export const fast2SMS = {
  apiKey: process.env.REACT_APP_FAST2SMS_API_KEY || '',

  sendOTP: async (mobile: string, otp: string): Promise<void> => {
    try {
      const response = await fetch('https://www.fast2sms.com/dev/bulkV2', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          route: 'otp',
          variables_values: otp,
          numbers: mobile,
        }),
      });

      const data = await response.json();
      
      if (data.return === false) {
        throw new Error(data.message || 'Failed to send SMS');
      }

      console.log('SMS sent successfully via Fast2SMS');
    } catch (error) {
      console.error('Fast2SMS error:', error);
      throw new Error('Failed to send OTP');
    }
  },
};

// MSG91 implementation (India)
export const msg91SMS = {
  apiKey: process.env.REACT_APP_MSG91_API_KEY || '',
  templateId: process.env.REACT_APP_MSG91_TEMPLATE_ID || '',

  sendOTP: async (mobile: string, otp: string): Promise<void> => {
    try {
      const response = await fetch(`https://api.msg91.com/api/v5/flow/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authkey': msg91SMS.apiKey,
        },
        body: JSON.stringify({
          flow_id: msg91SMS.templateId,
          sender: 'SEOEXP',
          mobiles: `91${mobile}`,
          VAR1: otp,
        }),
      });

      const data = await response.json();
      
      if (data.type !== 'success') {
        throw new Error(data.message || 'Failed to send SMS');
      }

      console.log('SMS sent successfully via MSG91');
    } catch (error) {
      console.error('MSG91 error:', error);
      throw new Error('Failed to send OTP');
    }
  },
};

// TypeScript declarations for EmailJS
declare global {
  interface Window {
    emailjs?: any;
  }
} 